import React, { useEffect, useState, useContext } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AuthenticationContext } from './App';
import { Spinner } from 'react-bootstrap';
import Header from './Header';
import Main from './Main';
import Footer from './Footer';
import { GetJson, PostJson } from './helpers/fetchReact';
import { REST_API } from './constants';
import DisabledAccount from '@awp/DisabledAccount';

import { fetchCurrentUser } from '@stores/usersSlice';
import {
  fetchRoles,
  fetchUserTypes,
  fetchAccessRequestStatuses,
  fetchPermissions,
  fetchObservationTypes as fetchObservationTypesDd,
  fetchAvalancheTechnicians,
  fetchHazardTechnicians,
  fetchInternalIngestionCandidateStatus,
  fetchNonAvalancheGeographData,
  fetchSensorTypes,
  fetchWeatherStationObservationTypes,
} from './stores/dropdownsSlice';

import {
  fetchObservationTypes,
  fetchValidationRules,
  fetchDerivedObservationIds,
} from '@awp/stores/dataVisualizationSlice';
import {
  fetchWeatherStations,
  fetchWeatherStationGeographicAreas,
  fetchWeatherStationSensorTypes,
  fetchWeatherstationStatuses as fetchWeatherstationAllStatuses,
} from '@awp/stores/weatherStationSlice';
import {
  fetchWeatherStationStatuses,
  fetchWeatherStationStatusTypes,
  fetchRegions,
} from '@awp/stores/weatherStationStatusesSlice';
import {
  fetchGraphVisualizationTypes,
  fetchStandardgraphaxises,
  fetchUnitofmeasures,
  fetchDataTypes,
  fetchTransformTypes,
} from '@awp/stores/observationTypeStatusesSlice';

import { fetchSensorstatuses, fetchSensormodels } from '@awp/stores/sensorEquipmentSlice';
import { fetchModelstatuses, fetchTypes, fetchManufacturers } from '@awp/stores/sensorModelSlice';
import { fetchGraphViews } from '@awp/stores/graphViewPageSlice';
import { fetchGraphPresets } from '@stores/graphPresetSlice';
import {
  fetchAvalanceObservationData,
  fetchAvalanceGeographicData,
  fetchGeograhpicAreaLines,
  fetchGeograhpicAreaPoints,
} from './stores/avalancheSlice';
import { fetchEventPropertyData, fetchEventTypeData } from './stores/eventSlice';
import {
  fetchHazardForecastDropDown,
  fetchHazardForecastLevelDropDown,
  fetchHazardTrendDropDown,
} from '@awp/stores/hazardForecastSlice';
import { fetchSystemNotifications } from '@awp/stores/systemNotificationsSlice';

import AccessRequest from '@components/AccessRequestAdmin/AccessRequest';
import { fetchUserPresets } from '@stores/userPresetSlice';
import { fetchSoilTypes } from '@stores/soilTypesSlice';

const fetchMaintenance = () => {
  return fetch('/__maintenance-checker', { method: 'GET' })
    .then((resp) => resp.text())
    .then((data) => data === 'true')
    .catch(() => false);
};

const AppMain = () => {
  const dispatch = useDispatch();
  const keycloak = useContext(AuthenticationContext);
  const [loading, setLoading] = useState(true);
  const [needAccessRequest, setNeedAccessRequest] = useState(false);
  const [accountDisabled, setAccountDisabled] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [inMaintenance, setInMaintenance] = useState(false);
  const [isBarExpanded, setIsBarExpanded] = useState(false);

  const navigationUrls = [
    '/',
    '/api/v1/users/currentuser',
    '/api/v1/dropdowns/observationtypes',
    '/api/v1/avalanche/observationdata',
    '/api/v1/dropdowns/avalanchetechnicians',
    '/api/v1/dropdowns/internalingestioncandidatestatus',
    '/api/v1/event/properties',
    '/api/v1/event/types',
    '/api/v1/avalanche/geographicdata',
    '/api/v1/avalanche/geographicarealines',
    '/api/v1/avalanche/geographicareapoints',
  ];

  const initialFetch = async () => {
    const maintenance = await fetchMaintenance();
    setInMaintenance(maintenance);

    const data = await GetJson(REST_API.USERS + 'currentuser')
      .then((response) => response.json())
      .catch(() => null);

    console.log('MAINTENANCE MODE:', maintenance);
    setCurrentUser(data);
    const hasAccess = data && data.Permissions && data.Permissions.length > 0 && data.IsActive;

    if (hasAccess) {
      setNeedAccessRequest(false);

      if (navigator.serviceWorker && keycloak.token) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.active.postMessage({ authHeader: `Bearer ${keycloak.token}`, navigationUrls });
        });
      }

      await Promise.all([
        dispatch(fetchCurrentUser()),
        dispatch(fetchRoles()),
        dispatch(fetchUserTypes()),
        dispatch(fetchAccessRequestStatuses()),
        dispatch(fetchPermissions()),
        dispatch(fetchObservationTypesDd()),
        dispatch(fetchObservationTypes()),
        dispatch(fetchDerivedObservationIds()),
        dispatch(fetchWeatherStations()),
        dispatch(fetchValidationRules()),
        dispatch(fetchWeatherStationStatuses()),
        dispatch(fetchWeatherStationStatusTypes()),
        dispatch(fetchRegions()),
        dispatch(fetchGraphVisualizationTypes()),
        dispatch(fetchStandardgraphaxises()),
        dispatch(fetchUnitofmeasures()),
        dispatch(fetchDataTypes()),
        dispatch(fetchTransformTypes()),
        dispatch(fetchSensormodels()),
        dispatch(fetchSensorstatuses()),
        dispatch(fetchModelstatuses()),
        dispatch(fetchTypes()), //model type
        dispatch(fetchManufacturers()),
        dispatch(fetchGraphViews()),
        dispatch(fetchGraphPresets()),
        dispatch(fetchAvalanceObservationData()),
        dispatch(fetchAvalanceGeographicData()),
        dispatch(fetchAvalancheTechnicians()),
        dispatch(fetchHazardTechnicians()),
        dispatch(fetchEventPropertyData()),
        dispatch(fetchInternalIngestionCandidateStatus()),
        dispatch(fetchNonAvalancheGeographData()),
        dispatch(fetchEventTypeData()),
        dispatch(fetchGeograhpicAreaLines()),
        dispatch(fetchGeograhpicAreaPoints()),
        dispatch(fetchWeatherstationAllStatuses()),
        dispatch(fetchSensorTypes()),
        dispatch(fetchWeatherStationGeographicAreas()),
        dispatch(fetchWeatherStationSensorTypes()),
        dispatch(fetchHazardForecastDropDown()),
        dispatch(fetchHazardForecastLevelDropDown()),
        dispatch(fetchHazardTrendDropDown()),
        dispatch(fetchWeatherStationObservationTypes()),
        dispatch(fetchSystemNotifications()),
        dispatch(fetchUserPresets()),
        dispatch(fetchSoilTypes()),
      ]);

      if (data?.DaysSinceLastLoginUpdate > 1) {
        await PostJson(REST_API.USERS + 'updateuserlogin').catch((ex) => console.error(ex));
      }
    } else if (!maintenance) {
      if (window.awpIsOnline && data) {
        if (!data.IsActive && !data.NeedAccessRequest) {
          setAccountDisabled(true);
        } else if (window.awpIsOnline) {
          setNeedAccessRequest(true);
        }
      }
    }

    setLoading(false);
  };

  useEffect(() => {
    initialFetch();
  }, []);

  if (loading)
    return (
      <div className="text-center mt-4">
        <p>Loading SAWSx</p>
        <Spinner animation="border" />
      </div>
    );

  const inEntireMaintenance = inMaintenance && !currentUser?.Version;
  console.log('ENTIRE MAINTENANCE MODE:', inEntireMaintenance);

  if (inEntireMaintenance) {
    return (
      <BrowserRouter>
        <Header
          maintenance={inMaintenance}
          inEntireMaintenance
          isBarExpanded={isBarExpanded}
          setIsBarExpanded={setIsBarExpanded}
        />
        <div
          style={{ height: 'calc(100vh - 250px)', textAlign: 'center', marginTop: '30px' }}
          className="wrapper flex-grow-1"
        >
          <h1>We&rsquo;ll Be Back Shortly!</h1>
          <div>
            <p>
              Apologies for the inconvenience, as we are currently undergoing essential maintenance.
              <br />
              Rest assured, we&rsquo;ll be back online in no time!
            </p>
          </div>
        </div>
        <Footer isBarExpanded={isBarExpanded} />
      </BrowserRouter>
    );
  }

  if (needAccessRequest)
    return (
      <BrowserRouter>
        <AccessRequest currentUser={currentUser} isBarExpanded={isBarExpanded} setIsBarExpanded={setIsBarExpanded} />
      </BrowserRouter>
    );

  if (accountDisabled)
    return (
      <BrowserRouter>
        <DisabledAccount isBarExpanded={isBarExpanded} setIsBarExpanded={setIsBarExpanded} />
      </BrowserRouter>
    );

  return (
    <BrowserRouter>
      <Header inMaintenance={inMaintenance} isBarExpanded={isBarExpanded} setIsBarExpanded={setIsBarExpanded} />
      <Main />
      <Footer isBarExpanded={isBarExpanded} />
    </BrowserRouter>
  );
};

export default AppMain;
